import { useState } from 'react';
import { IconSearch, IconBell, IconUpload, IconRefresh } from './icons';
import { triggerPublish } from '../lib/api';
import { useToast } from '../lib/toast';

interface Props {
  section: string;
  pendingChanges: number;
  onPublished?: () => void;
  onSearchClick?: () => void;
}

export function Topbar({ section, pendingChanges, onPublished, onSearchClick }: Props) {
  const [publishing, setPublishing] = useState(false);
  const [success, setSuccess] = useState(false);
  const toast = useToast();

  async function onPublish() {
    if (publishing) return;
    setPublishing(true);
    setSuccess(false);
    try {
      await triggerPublish();
      setSuccess(true);
      onPublished?.();
      toast.success('Build triggered', 'Public site rebuilds in ~90s');
      setTimeout(() => setSuccess(false), 6000);
    } catch (e) {
      toast.error('Publish failed', e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  }

  return (
    <header
      className="sticky top-0 z-20 bg-cream-tint/85 backdrop-blur border-b border-line-soft"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="px-4 md:px-8 h-[56px] md:h-[60px] flex items-center justify-between gap-3 md:gap-6">
        <div className="text-[15px] md:text-[13px] font-display md:font-sans md:text-ink-muted min-w-0">
          <span className="text-ink font-medium truncate">{section}</span>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Desktop: full search button. Mobile: just the icon. */}
          <button
            onClick={onSearchClick}
            className="hidden md:block relative w-[280px] h-9 pl-8 pr-14 text-[13px] text-left rounded-md border border-line bg-white hover:bg-cream-tint transition"
            title="Open command palette (⌘K)"
          >
            <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <span className="text-ink-faint">Search or run a command…</span>
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10.5px] font-mono text-ink-faint bg-cream-tint border border-line rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
          <button
            onClick={onSearchClick}
            className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-line bg-white active:scale-95 transition"
            title="Search"
            aria-label="Search"
          >
            <IconSearch className="w-4 h-4 text-ink-muted" />
          </button>

          <button className="hidden md:grid h-9 w-9 place-items-center rounded-md border border-line bg-white hover:bg-cream-tint" title="Notifications">
            <IconBell className="w-4 h-4 text-ink-muted" />
          </button>

          <button
            onClick={onPublish}
            disabled={publishing}
            className="group h-10 md:h-9 px-3 md:px-3.5 rounded-full md:rounded-md bg-navy text-white text-[13px] font-medium flex items-center gap-1.5 md:gap-2 hover:bg-navy-soft transition disabled:opacity-60 active:scale-95"
            aria-label="Publish"
          >
            {publishing ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconUpload className="w-4 h-4" />}
            <span className="hidden sm:inline">{publishing ? 'Publishing…' : success ? 'Build triggered' : 'Publish'}</span>
            {pendingChanges > 0 && !publishing && !success && (
              <span className="num h-5 min-w-5 px-1.5 -mr-0.5 grid place-items-center rounded-full bg-white/20 text-[11px] font-medium">{pendingChanges}</span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
